<template>
    <div v-if="isLoaded" class="page-wrap ep">
        <editor-bar :name="node.Name" :version="node.Version" :dirty="dirty" type-label="role sheet" @cancel="cancel()" @save="openSave()" @height="barHeight = $event">
            <button type="button" class="ai-btn pb-btn" title="Improve with AI" @click="showAIModal = true">
                <svg viewBox="0 0 24 24" class="ep-fill"><path d="M13 0L3.5 14H10l-1.5 10L20 9h-6.5L15 0h-2z"/></svg><span>Improve with AI</span>
            </button>
        </editor-bar>

        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <editor-mode-toggle :preview="showPreview" :changed-count="changedSeats.length" changed-label="seats" @edit="closePreview()" @preview="openPreview()" />
            <div v-if="showPreview" class="ep-preview">
                <playbook-view :preview-node="previewNode" :hide-bar="true" />
            </div>

            <div v-show="!showPreview" class="ep-layout">
                <div class="ep-main">
                    <div class="ep-fmt" :class="{ live: activeEditor }" :style="{ top: barHeight + 'px' }">
                        <span class="ep-fmt-where">{{ activeEditor && activeSection ? (seatLabel(current) || 'Seat') + ' · ' + (activeSection.label || 'Section') : 'Click into a section to format it' }}</span>
                        <div v-if="activeEditor" class="ep-fmt-btns" @mousedown="keepEditorFocus">
                            <tip-tap-toolbar :editor="activeEditor" :hide-h3="true" :hide-h4="true" :hide-table="true" :show-align="true" />
                        </div>
                    </div>

                    <article class="ep-paper">
                        <editor-doc-head :locs="locs" :deps="deps" :types="types" :owner-names="ownerNames" :version="node.Version" @open="showDetails = true" />

                        <div class="ve-help">
                            <svg viewBox="0 0 24 24"><path d="M8 13V4.5a1.5 1.5 0 0 1 3 0V12M11 11.5v-2a1.5 1.5 0 0 1 3 0V12M14 10.5a1.5 1.5 0 0 1 3 0V12M17 11.5a1.5 1.5 0 0 1 3 0V16a6 6 0 0 1-6 6h-2 .21a6 6 0 0 1-5-2.69L7 19c-.31-.48-1.4-2.38-3.26-5.7a1.5 1.5 0 0 1 .54-2.02 1.87 1.87 0 0 1 2.28.28L8 13" /></svg>
                            Pick a seat to edit it. Drag seats to reorder them. Each seat is one role on the sheet.
                        </div>
                        <div class="ve-lane-wrap">
                            <div class="ve-lane rs-lane">
                                <draggable :list="sheet.seats" class="rs-lane-seats" @start="dragStart" @end="dragEnd" :animation="150" ghost-class="kanban-ghost" :force-auto-scroll-fallback="true" :delay="200" :delay-on-touch-only="true" :touch-start-threshold="5">
                                    <button v-for="(seat, i) in seats" :key="seat.id" type="button" :id="'rs-seat-' + i" :class="['rs-seat', { on: selected == i }]" :aria-pressed="selected == i" @click="select(i)">
                                        <span class="rs-seat-i">{{ initials(seat) }}</span>
                                        <span class="rs-seat-n">{{ seatLabel(seat) || 'New seat' }}</span>
                                    </button>
                                </draggable>
                                <button type="button" class="ve-add rs-add" @click="addSeat()">
                                    <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg><span>Add seat</span>
                                </button>
                            </div>
                        </div>

                        <section v-if="current" class="rs-ed">
                            <div class="rs-ed-h">
                                <span class="rs-seat-i big">{{ initials(current) }}</span>
                                <!-- The role, and optionally who's in it (shown as "John - CEO") -->
                                <div class="rs-who-fields">
                                    <input ref="who" v-model="current.title" class="rs-who" placeholder="Title, e.g. CEO" aria-label="Seat title" />
                                    <input v-model="current.name" class="rs-who-name" placeholder="Name (optional), e.g. John" aria-label="Name of who's in this seat" />
                                </div>
                                <span class="rs-ed-tools">
                                    <button type="button" class="rs-icon" aria-label="Move seat left" :disabled="selected == 0" @click="moveSeat(selected, -1)"><svg viewBox="0 0 24 24"><path d="M19 12H5M11 18l-6-6 6-6" /></svg></button>
                                    <button type="button" class="rs-icon" aria-label="Move seat right" :disabled="selected == seats.length - 1" @click="moveSeat(selected, 1)"><svg viewBox="0 0 24 24"><path d="M5 12h14M13 18l6-6-6-6" /></svg></button>
                                    <button type="button" class="ep-chip" @click="duplicateSeat(selected)"><svg viewBox="0 0 24 24"><path d="M9 9h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2V9zM5 15H4a1 1 0 0 1-1-1V5a2 2 0 0 1 2-2h9a1 1 0 0 1 1 1v1" /></svg>Duplicate</button>
                                    <button type="button" class="ep-chip" @click="openPaste()"><svg viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 3h6v4H9zM9 12h6M9 16h4" /></svg>Paste text</button>
                                    <button type="button" class="rs-icon del" aria-label="Remove seat" @click="confirmRemoveSeat(selected)"><svg viewBox="0 0 24 24"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg></button>
                                </span>
                            </div>
                            <p class="rs-note">
                                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></svg>
                                Sections are shared by every seat. Renaming, moving or removing one changes it for all {{ seats.length }} seat{{ seats.length == 1 ? '' : 's' }}.
                            </p>

                            <!-- Structured accountabilities: also shown on the accountability chart and the employee page -->
                            <div class="rs-sec rs-acc">
                                <div class="rs-sec-h">
                                    <span class="rs-sec-l static">Accountabilities<span v-if="current.accountabilities.length"> ({{ current.accountabilities.length }})</span></span>
                                    <span class="rs-acc-hint">Also shown on the accountability chart and employee page</span>
                                </div>
                                <div v-for="(acc, k) in current.accountabilities" :key="k" class="rs-acc-row">
                                    <span class="rs-acc-n">{{ k + 1 }}</span>
                                    <input v-model="acc.value" class="rs-acc-v" placeholder="Accountability" />
                                    <input v-model="acc.value2" class="rs-acc-d" placeholder="Description (optional)" />
                                    <span class="rs-ed-tools">
                                        <button type="button" class="rs-icon" aria-label="Move up" :disabled="k == 0" @click="moveIn(current.accountabilities, k, -1)"><svg viewBox="0 0 24 24"><path d="M6 15l6-6 6 6" /></svg></button>
                                        <button type="button" class="rs-icon" aria-label="Move down" :disabled="k == current.accountabilities.length - 1" @click="moveIn(current.accountabilities, k, 1)"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg></button>
                                        <button type="button" class="rs-icon del" aria-label="Remove accountability" @click="current.accountabilities.splice(k, 1)"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
                                    </span>
                                </div>
                                <button type="button" class="ep-chip rs-acc-add" @click="addAccountability()"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>Add accountability</button>
                            </div>

                            <section v-for="(section, j) in sections" :key="section.id" class="rs-sec">
                                <div class="rs-sec-h">
                                    <input v-model="section.label" class="rs-sec-l" placeholder="Section name" aria-label="Section name" title="Renames this section on every seat" />
                                    <span class="rs-ed-tools">
                                        <button type="button" class="rs-icon" aria-label="Move section up" :disabled="j == 0" @click="moveIn(sections, j, -1)"><svg viewBox="0 0 24 24"><path d="M6 15l6-6 6 6" /></svg></button>
                                        <button type="button" class="rs-icon" aria-label="Move section down" :disabled="j == sections.length - 1" @click="moveIn(sections, j, 1)"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" /></svg></button>
                                        <button type="button" class="rs-icon del" aria-label="Remove section from every seat" @click="confirmRemoveSection(j)"><svg viewBox="0 0 24 24"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></svg></button>
                                    </span>
                                </div>
                                <p v-if="section.hint" class="rs-sec-hint">{{ section.hint }}</p>
                                <div class="rs-body">
                                    <tip-tap :key="current.id + section.id" :model-value="current.values[section.id] || ''" :hide-toolbar="true" :hide-h3="true" :hide-h4="true" :hide-table="true" :show-align="true" @update:model-value="setValue(section.id, $event)" @focus="onEditorFocus($event, section)" />
                                </div>
                            </section>
                            <button type="button" class="ep-addlast" @click="addSection()">
                                <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>Add section to every seat
                            </button>
                        </section>
                        <p v-else class="ep-empty">No seats yet. Add one to get started.</p>
                    </article>
                </div>

                <aside class="ep-side" aria-label="Outline" :style="{ top: barHeight + 'px' }">
                    <div class="ep-side-h">Seats</div>
                    <draggable :list="sheet.seats" @start="dragStart" @end="dragEnd" :animation="150" ghost-class="kanban-ghost">
                        <button v-for="(seat, i) in seats" :key="seat.id" type="button" :class="['ep-ol', { on: selected == i }]" @click="select(i)">
                            <span>{{ i + 1 }}</span>{{ seatLabel(seat) || 'New seat' }}
                        </button>
                    </draggable>
                    <editor-activity :comments="comments" :signed-count="signedCount" :required-count="requiredSigners.length" :version="node.Version" />
                </aside>
            </div>
        </div>

        <div v-if="showPaste" class="error-msg-wrap">
            <div class="error-msg ep-save rs-paste">
                <h3>Paste text into {{ seatLabel(current) || 'this seat' }}</h3>
                <p>Paste a role description. A line that matches a section name, like "Key Responsibilities", starts that section, and the lines under it become its bullet points. Other headings that end with ":" become new sections, and lines under an "Accountabilities" heading become accountabilities.</p>
                <textarea v-model="pasteText" rows="10" placeholder="Your Main Purpose:&#10;Keep the whole company pointed in the right direction.&#10;Key Responsibilities:&#10;- Set annual and quarterly Rocks" aria-label="Text to paste"></textarea>
                <p class="rs-paste-sum">{{ pasteSummary }}</p>
                <div class="ep-save-btns">
                    <button type="button" class="btn-light" @click="showPaste = false">Cancel</button>
                    <button type="button" :class="pasteResult.count ? 'btn' : 'btn disable-button'" @click="applyPaste()">Paste</button>
                </div>
            </div>
        </div>

        <confirm-delete v-if="confirmAction" :msg="confirmMsg" @confirm="runConfirm()" @cancel="confirmAction = null" />
        <editor-details-drawer v-if="showDetails" v-model:name="node.Name" v-model:ownerIds="ownerIDs" v-model:locs="locs" v-model:deps="deps" v-model:types="types" type-label="role sheet" @close="showDetails = false" />
        <editor-save-dialog v-if="showSave" v-model:status="node.NeedsApprovalType" :version="node.Version" :required-count="requiredSigners.length" :signed-count="signedCount" :busy="disableButton" note-placeholder="e.g. Added the Recurring Meetings section" @cancel="showSave = false" @save="submitCheck($event)" />
        <editor-discard-dialog v-if="showDiscard" @keep="showDiscard = false" @discard="leave()" />
        <ai-improve-panel v-if="showAIModal" node-type="sheet" :slug="node.Slug" :current-draft="{ name: node.Name, sheet: sheet }" @close="showAIModal = false" @apply="d => { onAIDraft(d); showAIModal = true }" @accept="onAIDraft" />
        <confirm-delete v-if="showAIConfirm" @confirm="submit()" @cancel="() => { showAIConfirm = false; }" :msg="AIConfirmText"/>
    </div>
</template>

<script lang="ts">
import { defineComponent, toRaw } from 'vue';
import TipTap from '@/components/TipTap.vue';
import TipTapToolbar from '@/components/TipTapToolbar.vue';
import PlaybookView from '@/views/Playbook.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';
import EditorBar from '@/components/EditorBar.vue';
import EditorModeToggle from '@/components/EditorModeToggle.vue';
import EditorDocHead from '@/components/EditorDocHead.vue';
import EditorActivity from '@/components/EditorActivity.vue';
import EditorDetailsDrawer from '@/components/EditorDetailsDrawer.vue';
import EditorSaveDialog from '@/components/EditorSaveDialog.vue';
import EditorDiscardDialog from '@/components/EditorDiscardDialog.vue';
import AIImprovePanel from '@/components/AIImprovePanel.vue';
import { VueDraggableNext } from 'vue-draggable-next';
import nodeEditor from '@/nodeEditor';
import { readRoleSheet, newSeat, newSection, seatInitials, seatLabel, plainText, labelKey } from '@/roleSheet';

function cleanHtml(html: any): string {
    return plainText(html) ? `${html}` : '';
}

function escapeHtml(text: string): string {
    return `${text}`.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export default defineComponent({
    name: 'EditSheet',
    mixins: [ nodeEditor ],
    components: {
        PlaybookView,
        draggable: VueDraggableNext,
        TipTap,
        TipTapToolbar,
        ConfirmDelete,
        EditorBar,
        EditorModeToggle,
        EditorDocHead,
        EditorActivity,
        EditorDetailsDrawer,
        EditorSaveDialog,
        EditorDiscardDialog,
        AiImprovePanel: AIImprovePanel
    },
    data() {
        return {
            sheet: { v: 2, sections: [], seats: [] } as any,
            savedSeats: {} as any,
            selected: 0,
            draggingSelectedId: '',
            activeEditor: null as any,
            activeSection: null as any,
            showPaste: false,
            pasteText: '',
            confirmAction: null as any,
            confirmMsg: '',
            showAIModal: false
        }
    },
    computed: {
        seats(): any[] {
            return this.sheet.seats || [];
        },
        sections(): any[] {
            return this.sheet.sections || [];
        },
        current(): any {
            return this.seats[this.selected] || null;
        },
        // Seats that differ from the saved version (any section text, name or accountabilities)
        changedSeats(): string[] {
            return this.seats.filter((seat: any) => this.savedSeats[seat.id] != JSON.stringify(this.normalizeSeat(seat))).map((seat: any) => seat.id);
        },
        pasteResult(): any {
            return this.parsePaste(this.pasteText);
        },
        pasteSummary(): string {
            const r = this.pasteResult;
            if(!this.pasteText.trim()) return 'Nothing pasted yet.';
            if(!r.count) return 'No section headings found. Start each part with a section name on its own line.';
            const parts = [];
            if(r.filled.length) parts.push(`Fills ${r.filled.length} section${r.filled.length == 1 ? '' : 's'}: ${r.filled.join(', ')}`);
            if(r.added.length) parts.push(`adds ${r.added.length} new: ${r.added.join(', ')}`);
            if(r.accountabilities.length) parts.push(`${r.accountabilities.length} accountabilit${r.accountabilities.length == 1 ? 'y' : 'ies'}`);
            return parts.join(' · ') + '. Existing text in those sections is replaced.' + (r.skipped ? ` ${r.skipped} line(s) before the first heading are skipped.` : '');
        }
    },
    beforeRouteLeave(to: any, from: any, next: any) {
        this.guardLeave(next);
    },
    methods: {
        // nodeEditor content hooks
        contentField(): string {
            return 'SheetJSON';
        },
        // Opens either shape: sheets not migrated yet are converted the same way the migration does
        loadContent() {
            this.sheet = readRoleSheet(this.node);
            this.select(0);
        },
        contentData(): any {
            return this.sheet;
        },
        contentSnapshot(): any {
            return {
                sections: this.sections.map((s: any) => ({ id: s.id, label: s.label })),
                seats: this.seats.map((seat: any) => this.normalizeSeat(seat))
            };
        },
        onMarkSaved() {
            this.savedSeats = {};
            for(const seat of this.seats) this.savedSeats[seat.id] = JSON.stringify(this.normalizeSeat(seat));
        },
        // SheetColsJSON mirrors each seat's id + accountabilities (what the old cols list held)
        extraFields(): any {
            return { SheetColsJSON: JSON.stringify(this.seats.map((s: any) => ({ id: s.id, accountabilities: s.accountabilities }))) };
        },
        beforeSave() {
            const sectionIds = this.sections.map((s: any) => s.id);
            for(const seat of this.seats) {
                seat.name = `${seat.name || ''}`.trim();
                seat.title = `${seat.title || ''}`.trim();
                seat.accountabilities = seat.accountabilities.filter((a: any) => `${a.value || ''}`.trim() || `${a.value2 || ''}`.trim());
                const values: any = {};
                for(const id of sectionIds) {
                    if(cleanHtml(seat.values[id])) values[id] = seat.values[id];
                }
                seat.values = values;
            }
            for(const section of this.sections) section.label = `${section.label || ''}`.trim();
        },
        typeTitle(): string {
            return 'Role Sheet';
        },

        normalizeSeat(seat: any): any {
            return {
                name: seat.name || '',
                title: seat.title || '',
                accountabilities: (seat.accountabilities || []).map((a: any) => ({ value: a.value || '', value2: a.value2 || '' })),
                values: this.sections.map((s: any) => cleanHtml(seat.values[s.id]))
            };
        },
        initials(seat: any): string {
            return seatInitials(seat);
        },
        seatLabel,
        select(i: number) {
            this.selected = Math.max(0, Math.min(i, this.seats.length - 1));
            this.activeEditor = null;
            this.activeSection = null;
        },
        setValue(sectionId: string, html: string) {
            this.current.values[sectionId] = html;
        },
        onEditorFocus(editor: any, section: any) {
            this.activeEditor = editor;
            this.activeSection = section;
        },
        // Clicking a toolbar button shouldn't take the cursor out of the section being formatted
        keepEditorFocus(e: any) {
            if(e.target.tagName != 'INPUT' && e.target.tagName != 'SELECT') e.preventDefault();
        },
        moveIn(list: any[], i: number, dir: number) {
            const j = i + dir;
            if(j < 0 || j >= list.length) return;
            [list[i], list[j]] = [list[j], list[i]];
        },
        focusSeat(i: number) {
            this.$nextTick(() => {
                const who = this.$refs.who as HTMLInputElement;
                if(who) who.focus();
                const chip = document.getElementById('rs-seat-' + i);
                if(chip) chip.scrollIntoView({ block: 'nearest', inline: 'center' });
            });
        },
        addSeat() {
            this.sheet.seats.push(newSeat());
            this.select(this.seats.length - 1);
            this.focusSeat(this.selected);
        },
        duplicateSeat(i: number) {
            const source = this.seats[i];
            // "(copy)" goes on the title, or the name when there's no title
            const copy = source.title ? newSeat(source.name, source.title + ' (copy)') : newSeat(source.name ? source.name + ' (copy)' : '');
            copy.accountabilities = JSON.parse(JSON.stringify(toRaw(source.accountabilities)));
            copy.values = JSON.parse(JSON.stringify(toRaw(source.values)));
            this.sheet.seats.splice(i + 1, 0, copy);
            this.select(i + 1);
            this.focusSeat(i + 1);
        },
        moveSeat(i: number, dir: number) {
            this.moveIn(this.sheet.seats, i, dir);
            this.select(i + dir);
        },
        // Dragging a seat reorders the list; keep the seat being edited selected wherever it lands
        dragStart() {
            this.draggingSelectedId = this.current ? this.current.id : '';
        },
        dragEnd() {
            const i = this.seats.findIndex((s: any) => s.id == this.draggingSelectedId);
            if(i >= 0 && i != this.selected) this.selected = i;
        },
        confirmRemoveSeat(i: number) {
            const seat = this.seats[i];
            this.confirmMsg = `Remove ${seatLabel(seat) ? '"' + seatLabel(seat) + '"' : 'this seat'} from the role sheet? Anyone whose role is this seat loses their role link.`;
            this.confirmAction = () => {
                this.sheet.seats.splice(i, 1);
                this.select(Math.max(0, i - 1));
            };
        },
        addAccountability() {
            this.current.accountabilities.push({ value: '', value2: '' });
            this.$nextTick(() => {
                const inputs = document.querySelectorAll('.rs-acc-v');
                const last = inputs[inputs.length - 1] as HTMLInputElement;
                if(last) last.focus();
            });
        },
        addSection() {
            this.sheet.sections.push(newSection(''));
            this.$nextTick(() => {
                const inputs = document.querySelectorAll('.rs-sec-l:not(.static)');
                const last = inputs[inputs.length - 1] as HTMLInputElement;
                if(last) last.focus();
            });
        },
        confirmRemoveSection(j: number) {
            const section = this.sections[j];
            const filled = this.seats.filter((s: any) => cleanHtml(s.values[section.id])).length;
            const remove = () => {
                this.sheet.sections.splice(j, 1);
                for(const seat of this.seats) delete seat.values[section.id];
                this.activeEditor = null;
                this.activeSection = null;
            };
            if(!filled) {
                remove();
                return;
            }
            this.confirmMsg = `Remove "${section.label || 'this section'}" from every seat? ${filled} seat${filled == 1 ? ' has' : 's have'} text in it, which will be deleted.`;
            this.confirmAction = remove;
        },
        runConfirm() {
            const action = this.confirmAction;
            this.confirmAction = null;
            if(action) action();
        },
        openPaste() {
            this.pasteText = '';
            this.showPaste = true;
        },
        // Section headings: a line matching a section name, or a short line ending with ":"
        parsePaste(text: string): any {
            const result: any = { sections: [] as any[], accountabilities: [] as any[], filled: [] as string[], added: [] as string[], skipped: 0, count: 0 };
            let current: any = null;
            for(const raw of `${text || ''}`.split(/\r?\n/)) {
                const line = raw.trim();
                if(!line) continue;
                const asHeading = line.replace(/:\s*$/, '');
                const key = labelKey(asHeading);
                const isAcc = /^accountabilit/.test(key);
                const existing = this.sections.find((s: any) => labelKey(s.label) && labelKey(s.label) == key);
                if(isAcc && asHeading.length <= 60) {
                    current = { acc: true };
                    continue;
                }
                if(existing || (/:\s*$/.test(line) && asHeading.length <= 60)) {
                    current = result.sections.find((s: any) => s.key == key);
                    if(!current) {
                        current = { key, label: existing ? existing.label : asHeading, id: existing ? existing.id : null, lines: [] as string[] };
                        result.sections.push(current);
                        (existing ? result.filled : result.added).push(current.label);
                    }
                    continue;
                }
                const item = line.replace(/^([-•*·▪●]|\d+[.)])\s*/, '');
                if(!current) {
                    result.skipped++;
                } else if(current.acc) {
                    const [value, ...rest] = item.split(/\s[-–—:]\s/);
                    result.accountabilities.push({ value: value.trim(), value2: rest.join(' - ').trim() });
                } else {
                    current.lines.push(item);
                }
            }
            result.count = result.sections.length + result.accountabilities.length;
            return result;
        },
        applyPaste() {
            const result = this.pasteResult;
            if(!result.count) return;
            for(const parsed of result.sections) {
                let id = parsed.id;
                if(!id) {
                    const section = newSection(parsed.label);
                    this.sheet.sections.push(section);
                    id = section.id;
                }
                this.current.values[id] = parsed.lines.length ? `<ul>${parsed.lines.map((l: string) => `<li><p>${escapeHtml(l)}</p></li>`).join('')}</ul>` : '';
            }
            if(result.accountabilities.length) this.current.accountabilities = result.accountabilities;
            this.activeEditor = null;
            this.activeSection = null;
            this.showPaste = false;
        },
        // "Improve with AI": seats keep their ids, so the people linked to them stay linked
        onAIDraft(draft: any) {
            const keepId = this.current && this.current.id;
            if (draft.name) this.node.Name = draft.name;
            this.activeEditor = null;
            this.activeSection = null;
            this.sheet = { ...this.sheet, ...draft.sheet };
            const i = this.seats.findIndex((seat: any) => seat.id == keepId);
            this.select(i >= 0 ? i : 0);
            this.showAIModal = false;
        }
    }
});
</script>
